/**
 * @license
 * Copyright Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
var _CardComponent_internals, _a;
import { __classPrivateFieldGet, __classPrivateFieldSet, __decorate } from "tslib";
import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { toggleState } from '../core/utils/utils';
import { CUSTOM_ELEMENT_NAME_PROPERTY } from '@tylertech/forge-core';
import { BaseLitElement } from '../core/base/base-lit-element';
const styles = ':host{display:block}:host([hidden]){display:none}.forge-card{--_card-background:var(--forge-card-background, var(--forge-theme-surface, #ffffff));--_card-height:var(--forge-card-height, 100%);--_card-width:var(--forge-card-width, 100%);--_card-outline-color:var(--forge-card-outline-color, var(--forge-theme-outline, #e0e0e0));--_card-outline-width:var(--forge-card-outline-width, var(--forge-border-thin, 1px));--_card-outline-style:var(--forge-card-outline-style, solid);--_card-elevation:var(--forge-card-elevation, none);--_card-padding:var(--forge-card-padding, var(--forge-spacing-medium, 16px));--_card-shape:var(--forge-card-shape, calc(var(--forge-shape-medium, 4px) * var(--forge-shape-factor, 1)));--_card-overflow:var(--forge-card-overflow, initial);--_card-raised-elevation:var(--forge-card-raised-elevation, 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12));--_card-raised-outline-width:var(--forge-card-raised-outline-width, 0)}.forge-card{background:var(--_card-background);border-color:var(--_card-outline-color);border-width:var(--_card-outline-width);border-style:var(--_card-outline-style);border-radius:var(--_card-shape);box-shadow:var(--_card-elevation);box-sizing:border-box;padding:var(--_card-padding);overflow:var(--_card-overflow);height:var(--_card-height);width:var(--_card-width);-webkit-tap-highlight-color:transparent}:host(:where([raised],:state(raised))) .forge-card{--_card-elevation:var(--_card-raised-elevation);--_card-outline-width:var(--_card-raised-outline-width)}:host([no-padding]) .forge-card{--_card-padding:var(--forge-card-padding, 0)}';
export const CARD_TAG_NAME = 'forge-card';
/**
 * @tag forge-card
 *
 * @attribute {boolean} [no-padding=false] - Removes the default padding from the card.
 *
 * @cssproperty --forge-card-background - The background color of the card.
 * @cssproperty --forge-card-height - The height of the card.
 * @cssproperty --forge-card-width - The width of the card.
 * @cssproperty --forge-card-outline-color - The outline color of the card.
 * @cssproperty --forge-card-outline-width - The outline width of the card.
 * @cssproperty --forge-card-outline-style - The outline style of the card.
 * @cssproperty --forge-card-elevation - The elevation/shadow of the card.
 * @cssproperty --forge-card-padding - The padding of the card.
 * @cssproperty --forge-card-shape - The shape (border-radius) of the card.
 * @cssproperty --forge-card-overflow - The overflow of the card.
 * @cssproperty --forge-card-raised-elevation - The elevation/shadow of the card when raised.
 * @cssproperty --forge-card-raised-outline-width - The outline width of the card when raised.
 *
 * @csspart root - The root container element.
 *
 * @state raised - The state of the card when raised.
 *
 * @cssclass forge-card - The card container element _(required)_.
 * @cssclass forge-card--raised - The card container element when raised _(required)_.
 */
let CardComponent = class CardComponent extends BaseLitElement {
    constructor() {
        super();
        _CardComponent_internals.set(this, void 0);
        /** Whether the card has elevation or not. */
        this.raised = false; // TODO: remove reflect in v4 in favor of :state
        __classPrivateFieldSet(this, _CardComponent_internals, this.attachInternals(), "f");
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('raised')) {
            toggleState(__classPrivateFieldGet(this, _CardComponent_internals, "f"), 'raised', this.raised);
        }
    }
    render() {
        return html `<div class="forge-card" part="root"><slot></slot></div>`;
    }
};
_CardComponent_internals = new WeakMap();
_a = CUSTOM_ELEMENT_NAME_PROPERTY;
CardComponent.styles = unsafeCSS(styles);
/** @deprecated Used for compatibility with legacy Forge @customElement decorator. */
CardComponent[_a] = CARD_TAG_NAME;
__decorate([
    property({ type: Boolean, reflect: true })
], CardComponent.prototype, "raised", void 0);
CardComponent = __decorate([
    customElement(CARD_TAG_NAME)
], CardComponent);
export { CardComponent };
